import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-CA");
};
const addMonth = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString("en-CA");
};
const PICKS = [["+1 day", (e) => addDays(e, 1)], ["+1 week", (e) => addDays(e, 7)], ["+1 month", addMonth]];

export const ExtendHireDialog = ({ order, onClose, onSaved }) => {
  const [endDate, setEndDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) { setEndDate(addDays(order.end_date, 1)); setPreview(null); }
  }, [order]);

  useEffect(() => {
    if (!order || !endDate) return;
    setError("");
    api.patch(`/hire-orders/${order.id}/extend`, { end_date: endDate, dry_run: true })
      .then((r) => setPreview(r.data))
      .catch((err) => { setPreview(null); setError(apiError(err.response?.data?.detail)); });
  }, [order, endDate]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/hire-orders/${order.id}/extend`, { end_date: endDate });
      toast.success(`Hire extended to ${endDate}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="extend-hire-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter">Extend Hire</DialogTitle>
        </DialogHeader>
        {order && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">{order.equipment_name} · {order.customer}<br />Currently ends <b>{order.end_date}</b></p>
            <div className="flex flex-wrap gap-2">
              {PICKS.map(([label, fn]) => {
                const val = fn(order.end_date);
                return (
                  <button key={label} type="button" onClick={() => setEndDate(val)} data-testid={`extend-pick-${label.replace(/[^a-z0-9]/gi, "")}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${endDate === val ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">New end date</label>
              <input type="date" min={addDays(order.end_date, 1)} value={endDate} onChange={(e) => setEndDate(e.target.value)}
                data-testid="extend-end-date-input" className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-brand" />
            </div>
            <div className="bg-zinc-50 border border-zinc-200 p-3 text-sm" data-testid="extend-preview">
              {error ? <span className="text-red-600">{error}</span> : preview ? (
                <div className="flex justify-between">
                  <span className="text-zinc-500">{money(order.total_cost)} →</span>
                  <span className="font-bold text-zinc-900" data-testid="extend-new-total">{money(preview.total_cost)} <span className="font-normal text-zinc-500">({preview.units} {preview.unit_label})</span></span>
                </div>
              ) : <span className="text-zinc-400">Calculating…</span>}
            </div>
          </div>
        )}
        <DialogFooter>
          <button onClick={save} disabled={saving || !preview} data-testid="extend-confirm-button"
            className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60">
            {saving ? "Extending…" : "Extend hire"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
